///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2026
//                           RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState, Fragment, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash/debounce";

import UploadIcon from "../../icons/UploadIcon";
import DownloadIcon from "../../icons/DownloadIcon";
import Spinner from "../../icons/Spinner";
import {
  openDeleteConfirmationModal,
  resetDeleteItemState
} from "../../../slices/bidSummarySlice";
import {
  filterPricingLanes,
  saveEditedLane,
  saveLaneEditsReset,
  fetchPricingLanes,
  resetPricingGridFilters,
  resetColumnOrder,
  deleteSingleLane,
  deleteAllLanes,
  getLaneMatchCountForTLLanes,
  initiateManualReprice,
  resetManualReprice,
  checkLaneRepriceInProgress,
  showRepriceInProgressModal,
  showImportInProgressModal,
  checkLaneImportInProgress,
  updateServerPagedPricingFilters,
  refetchRxoTlPricingPage,
  resetRxoTlPricingFiltersAndRefetch
} from "../../../slices/bidPricingSlice";
import {
  getEffectiveRoleForLanes,
  shouldUseRxoTlServerPaging
} from "../../../helpers/rxoTlPricingPagedSearch";
import UploadLanesModal from "./UploadLanesModal";
import {
  getLanesTemplateWithData,
  getLanesTemplateWithDataV2
} from "../../../api/lanes";
import {
  userRole,
  modeName,
  deleteItem,
  division as divisionType,
  status as bidStatus
} from "../../../helpers/types";
import getModeForUserRole from "../../../helpers/getModeForUserRole";
import { MIN_LANE_SEARCH_CHARACTERS } from "../../../helpers/filterPricingLanes";
import saveFile from "../../../helpers/saveFile";
import Alert from "../../Alert";
import ConfirmCancellationModal from "../details/ConfirmCancellationModal";
import EditIcon from "../../icons/EditIcon";
import ClearFilterIcon from "../../icons/ClearFilterIcon";
import ResetIcon from "../../icons/ResetIcon";
import DollarIcon from "../../icons/DollarIcon";
import ApplyMassMarginModal from "./ApplyMassMarginModal";
import BulkEditModal from "./BulkEditModal";
import DeleteIcon from "../../icons/DeleteIcon";
import DeleteConfirmationModal from "../DeleteConfirmationModal";
import IconButton from "../../form-controls/IconButton";
import messages from "../../../helpers/messages";
import RepriceIcon from "../../icons/RepriceIcon";
import ColumnChooser from "./ColumnChooser";

const RXO_TL_SERVER_SEARCH_DEBOUNCE_MS = 300;

export const Toolbar = ({
  bidId,
  error,
  role,
  division,
  unsavedChanges,
  disableDeleteButton,
  hasDisabledLanes,
  disableRepriceButton
}) => {
  const dispatch = useDispatch();
  const {
    bidSummary: {
      summary,
      deleteItem: { modalOpen, item, isLoading, isFailure, itemType }
    },
    bidPricing: {
      lanes,
      searchTerm,
      filtersApplied,
      saveLaneEditsLoading,
      saveLaneEditsFailure,
      columns: { changed: columnOrderChanged, order: columnsOrder },
      isLaneImportInProgress,
      manualReprice,
      pricingProgress: { isLaneRepriceInProgress },
      repriceInProgressModal,
      division: bidPricingDivision
    },
    user: { user },
    featureFlags: { enableAsyncLaneExport, enableRxoTlPricingServerPagination }
  } = useSelector(state => state);

  const useServerPagedSearch = shouldUseRxoTlServerPaging({
    enableRxoTlPricingServerPagination,
    isCoyoteBid: summary?.isCoyoteBid,
    bidDivision: bidPricingDivision ?? summary?.division,
    effectiveRoleForLanes: getEffectiveRoleForLanes(role, user)
  });

  const debouncedRxoTlSearchRefetch = useMemo(
    () =>
      debounce(() => {
        dispatch(
          refetchRxoTlPricingPage({
            bidId,
            role,
            shouldFetchMatchingLanes: false
          })
        );
      }, RXO_TL_SERVER_SEARCH_DEBOUNCE_MS),
    [dispatch, bidId, role]
  );

  useEffect(
    () => () => {
      debouncedRxoTlSearchRefetch.cancel();
    },
    [debouncedRxoTlSearchRefetch]
  );

  const [cancelChangesModal, toggleCancelChangesModal] = useState(false);
  const [uploadLanesModal, toggleUploadLanesModal] = useState(false);
  const [downloadLanesProgress, toggleDownloadLanesProgress] = useState(false);
  const [downloadLanesError, toggleDownloadLanesError] = useState(false);
  const [bulkEditModal, toggleBulkEditModal] = useState(false);
  const [applyMassMarginModal, toggleApplyMassMarginModal] = useState(false);
  const [showLaneExportInProgressModal, setShowLaneExportInProgressModal] =
    useState(false);

  const handleLanesDownload = () => {
    if (downloadLanesProgress === false) {
      toggleDownloadLanesProgress(true);
      if (enableAsyncLaneExport) {
        getLanesTemplateWithDataV2({
          bidId,
          userRoleForLanesWhenManagementUser: user.userRoleForManagementUser
        })
          .then(({ data }) => {
            toggleDownloadLanesProgress(false);
            setShowLaneExportInProgressModal(true);
          })
          .catch(() => {
            toggleDownloadLanesProgress(false);
            toggleDownloadLanesError(true);
          });
      } else {
        getLanesTemplateWithData({
          bidId,
          userRoleForLanesWhenManagementUser: user.userRoleForManagementUser
        })
          .then(({ data }) => {
            saveFile(
              data,
              `${summary.name} ${modeName[getModeForUserRole(role)]}.xlsx`
            );
            toggleDownloadLanesProgress(false);
          })
          .catch(() => {
            toggleDownloadLanesProgress(false);
            toggleDownloadLanesError(true);
          });
      }
    }
  };

  const handleCancelDialogExit = () => {
    toggleCancelChangesModal(false);
    dispatch(
      fetchPricingLanes({ bidId, role, isCoyoteBid: summary?.isCoyoteBid })
    );
  };
  const handleCancelDialogStay = () => toggleCancelChangesModal(false);

  const handleDeleteClick = () => {
    if (itemType === deleteItem.singleLane) {
      dispatch(deleteSingleLane(bidId, item, role, division));
    } else {
      dispatch(deleteAllLanes(bidId, role, division));
    }
  };

  const closeSystemErrorModal = () => {
    dispatch(resetDeleteItemState());
    dispatch(
      fetchPricingLanes({ bidId, role, isCoyoteBid: summary?.isCoyoteBid })
    );
  };

  const handleDeleteAll = () =>
    dispatch(
      openDeleteConfirmationModal({
        item: bidId,
        itemType: deleteItem.allLanes
      })
    );

  const laneImportCallbackSuccess = value => {
    if (value) {
      dispatch(showImportInProgressModal(true));
    } else {
      dispatch(initiateManualReprice(bidId, role, division));
    }
  };

  const laneRepriceCallbackSuccess = value => {
    if (value) {
      dispatch(showRepriceInProgressModal(true));
    } else {
      dispatch(
        checkLaneImportInProgress(bidId, laneImportCallbackSuccess, () =>
          dispatch(initiateManualReprice(bidId, role, division))
        )
      );
    }
  };

  const handleManualReprice = () => {
    dispatch(
      checkLaneRepriceInProgress(bidId, laneRepriceCallbackSuccess, () =>
        dispatch(initiateManualReprice(bidId, role, division))
      )
    );
  };
  const userMode = getModeForUserRole(role);
  const isSaveDisabled = !summary?.isCoyoteBid && isLaneRepriceInProgress;
  const saveTooltip = !summary?.isCoyoteBid && isLaneRepriceInProgress
    ? "Changes cannot be saved while pricing is in progress."
    : "";
  return (
    <Fragment>
      <ApplyMassMarginModal
        open={applyMassMarginModal}
        closeModal={() => toggleApplyMassMarginModal(false)}
        role={role}
        division={division}
      />
      {modalOpen && (
        <DeleteConfirmationModal
          handleClose={() => dispatch(resetDeleteItemState())}
          isLoading={isLoading}
          isFailure={isFailure}
          handleDeleteClick={handleDeleteClick}
          closeSystemErrorModal={closeSystemErrorModal}
          warningMessage={
            itemType === deleteItem.singleLane
              ? messages.deleteLaneConfirmationMessage.defaultMessage
              : messages.deleteAllLanesConfirmationMessage.defaultMessage
          }
          title={
            itemType === deleteItem.singleLane
              ? messages.deleteLane.defaultMessage
              : messages.deleteAllLanes.defaultMessage
          }
        />
      )}
      {repriceInProgressModal && (
        <Alert
          title={messages.processing.defaultMessage}
          message={messages.anotherRepriceInProgressMessage.defaultMessage}
          handleClose={() => dispatch(showRepriceInProgressModal(false))}
          open={repriceInProgressModal}
          large={true}
        />
      )}
      {showLaneExportInProgressModal && (
        <Alert
          title={messages.processing.defaultMessage}
          message={messages.laneExportInProgressModalMessage.defaultMessage}
          handleClose={() => setShowLaneExportInProgressModal(false)}
          open={showLaneExportInProgressModal}
          large={true}
        />
      )}
      <BulkEditModal
        open={bulkEditModal}
        handleClose={() => toggleBulkEditModal(false)}
        bidId={bidId}
        role={role}
        division={division}
      />
      <ConfirmCancellationModal
        open={cancelChangesModal}
        closeModal={handleCancelDialogStay}
        handleExit={handleCancelDialogExit}
      />
      <Alert
        handleClose={() => toggleDownloadLanesError(false)}
        message={messages.downloadDocumentErrorMessage.defaultMessage}
        open={downloadLanesError}
      />
      <Alert
        handleClose={() => dispatch(resetManualReprice())}
        message={messages.sorrySomethingWentWrongPleaseTryAgain.defaultMessage}
        open={manualReprice.isFailure}
      />

      <Alert
        handleClose={() => dispatch(saveLaneEditsReset())}
        message={messages.saveLanesErrorMessage.defaultMessage}
        open={saveLaneEditsFailure}
        modalStyle={{ minHeight: 190 }}
      />
      <div className="toolbar">
        <input
          value={searchTerm}
          className="filter"
          type="text"
          placeholder={messages.searchLanesInThisBid.defaultMessage}
          onChange={({ target }) => {
            if (useServerPagedSearch) {
              dispatch(
                updateServerPagedPricingFilters({ searchTerm: target.value })
              );
              if (target.value.trim().length === 0) {
                debouncedRxoTlSearchRefetch.cancel();
                dispatch(
                  refetchRxoTlPricingPage({
                    bidId,
                    role,
                    shouldFetchMatchingLanes: false
                  })
                );
              } else {
                debouncedRxoTlSearchRefetch();
              }
            } else {
              const next = target.value;
              const nextTrim = next.trim().length;
              const prev = searchTerm ?? "";
              const prevTrim = prev.trim().length;
              const shouldFetchLaneMatchCount =
                nextTrim === 0 ||
                nextTrim >= MIN_LANE_SEARCH_CHARACTERS ||
                (prevTrim >= MIN_LANE_SEARCH_CHARACTERS &&
                  nextTrim < prevTrim);
              dispatch(filterPricingLanes({ searchTerm: next, role }));
              if (shouldFetchLaneMatchCount) {
                dispatch(getLaneMatchCountForTLLanes());
              }
            }
          }}
        />
        <IconButton
          title={messages.exportLanes.defaultMessage}
          disabled={unsavedChanges}
          dataTestId="lanes-download-button"
          onClick={handleLanesDownload}
          inProgress={downloadLanesProgress}
          icon={DownloadIcon}
          viewBox="0 0 12 13"
          offset={[9, 6]}
        />
        <IconButton
          title={messages.importLanes.defaultMessage}
          disabled={
            (summary &&
              summary.modes?.some(
                x => x.mode === userMode && x.status === bidStatus.closed
              )) ||
            unsavedChanges ||
            error ||
            isLaneImportInProgress ||
            isLaneRepriceInProgress
          }
          dataTestId="upload-lanes-button"
          onClick={() => toggleUploadLanesModal(true)}
          icon={UploadIcon}
          offset={[9, 6]}
        />
        <IconButton
          title={messages.batchEdit.defaultMessage}
          disabled={
            unsavedChanges ||
            error ||
            isLaneImportInProgress ||
            isLaneRepriceInProgress
          }
          dataTestId="bulk-edit-button"
          onClick={() => toggleBulkEditModal(true)}
          icon={EditIcon}
          offset={[7, 6]}
          show={division !== divisionType.eu}
        />
        <IconButton
          title={messages.deleteAllLanes.defaultMessage}
          disabled={disableDeleteButton}
          dataTestId="delete-lanes-button"
          onClick={handleDeleteAll}
          icon={DeleteIcon}
          offset={[7, 6]}
        />

        <IconButton
          title={messages.applyMargin.defaultMessage}
          dataTestId="mass-margin-button"
          onClick={() => toggleApplyMassMarginModal(true)}
          icon={DollarIcon}
          show={
            role === userRole.salesRep ||
            role === userRole.tlPricingAnalyst ||
            role === userRole.assetBasedPricingUser
          }
          offset={[5, 6]}
          disabled={
            !summary ||
            hasDisabledLanes ||
            isLaneImportInProgress ||
            isLaneRepriceInProgress
          }
        />
        <IconButton
          title={messages.clearFilters.defaultMessage}
          disabled={!filtersApplied}
          dataTestId="clear-filter-button"
          onClick={() =>
            useServerPagedSearch
              ? dispatch(resetRxoTlPricingFiltersAndRefetch({ bidId, role }))
              : dispatch(resetPricingGridFilters())
          }
          icon={ClearFilterIcon}
          offset={[7, 6]}
        />

        <IconButton
          title={messages.resetColumns.defaultMessage}
          disabled={!columnOrderChanged}
          dataTestId="reset-order-button"
          onClick={() =>
            dispatch(
              resetColumnOrder({ role, isCoyoteBid: summary?.isCoyoteBid })
            )
          }
          icon={ResetIcon}
          offset={[10, 6]}
        />

        <IconButton
          title={messages.manualReprice.defaultMessage}
          disabled={
            isLaneImportInProgress ||
            isLaneRepriceInProgress ||
            disableRepriceButton ||
            isLoading
          }
          dataTestId="manual-reprice-button"
          onClick={handleManualReprice}
          inProgress={manualReprice.isLoading}
          icon={RepriceIcon}
          offset={[10, 6]}
          viewBox="0 0 400 400"
          show={
            role === userRole.salesRep ||
            role === userRole.tlPricingAnalyst ||
            role === userRole.assetBasedPricingUser
          }
        />

        {lanes?.length > 0 && columnsOrder?.length > 0 && (
          <ColumnChooser role={role} division={division} />
        )}

        {(role === userRole.salesRep ||
          role === userRole.tlPricingAnalyst ||
          role === userRole.assetBasedPricingUser) && (
          <>
            <button
              style={{ minWidth: 80 }}
              className={
                "h-32 text-button font-size-14 fr font-bold mr-16" +
                (unsavedChanges ? "" : " hidden")
              }
              data-testid="cancel-save-lanes"
              onClick={() => toggleCancelChangesModal(true)}
            >
              {messages.cancel.defaultMessage}
            </button>
            <button
              data-testid="save-lanes"
              onClick={() =>
                dispatch(saveEditedLane(parseInt(bidId), role, division))
              }
              disabled={isSaveDisabled}
              title={saveTooltip}              
              className={
                "h-32 secondary-button font-size-14 px-16 fr mr-16 no-focus font-bold" +
                (unsavedChanges ? "" : " hidden")
              }
            >
              {saveLaneEditsLoading ? (
                <Spinner
                  fill="#69C764"
                  className="spinner"
                  height="16px"
                  width="16px"
                />
              ) : (
                messages.saveChanges.defaultMessage
              )}
            </button>
          </>
        )}
      </div>
      {uploadLanesModal && (
        <UploadLanesModal
          bidId={bidId}
          handleClose={() => toggleUploadLanesModal(false)}
          role={role}
          division={division}
          fetchAfterUpload={true}
          isLaneImport={true}
        />
      )}
    </Fragment>
  );
};

export default Toolbar;
